import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../Notifications/store/notificationsSlice';
import socketFactory from '../../../utils/socketFactory';
import {
  setRecorderStatus,
  setRecorderData,
} from '../slice/recorderSlice';
import NotificationBuilder from '../../Notifications/utils/NotificationBuilder';

/* ──────────────────────────────────────────────
   GLOBAL CONTROLLER
────────────────────────────────────────────── */

let startRecordingHandler = null;
let stopRecordingHandler = null;


export const registerRecordingHandlers = ({ onStart, onStop }) => {
  startRecordingHandler = onStart;
  stopRecordingHandler = onStop;
};

export const triggerRecordingStart = async () => {
  if (startRecordingHandler) await startRecordingHandler();
};

export const triggerRecordingStop = async () => {
  if (stopRecordingHandler) await stopRecordingHandler();
};

/* ──────────────────────────────────────────────
   HOOK
────────────────────────────────────────────── */

export function useRoomRecording(currentRoom, localAvatar) {
  const dispatch = useDispatch();
  const listenersRef = useRef([]);
  const cancelledRef = useRef(false); // ✅ guard contre le cleanup race condition

  useEffect(() => {
    if (!currentRoom?.room_id || !socketFactory.socket) return;

    const roomId = String(currentRoom.room_id);
    let cancelled = false;

    // Rejoindre la room — le backend doit écouter 'room:join'
    socketFactory.socket.emit('room:join', { roomId });
    // Demander un sync au cas où un enregistrement est déjà en cours
    socketFactory.socket.emit('recording:sync-request', { roomId });

    cancelledRef.current = false;

    const register = async () => {
      listenersRef.current.forEach(l => socketFactory.off(l));
      listenersRef.current = [];

      const l1 = await socketFactory.on(
        'recording:started',
        ({ data }) => {
          // Filtre — ignorer les événements des autres rooms
          if (String(data.roomId) !== roomId) return;
          if (cancelled) return; // ✅ guard

          const owner = data.userId === localAvatar?.id;

          dispatch(setRecorderData({
            recorderName: data.userName,
            isOwner: owner,
            startedAt: data.startedAt,
          }));
          dispatch(setRecorderStatus('recording'));
          const headerKey = owner
            ? 'recording_started_owner_header'
            : 'recording_started_other_header';

          const messageKey = owner
            ? 'recording_started_owner_message'
            : 'recording_started_other_message';

          // Only pass userName when it's not the owner recording
          const messageVars = owner ? {} : { userName: data.userName };

          const notification = NotificationBuilder.info()
              .withHeader(headerKey, true)
              .withMessage(messageKey, true, messageVars)
              .build();
          dispatch(addNotification(notification));
        }
      );

      const l2 = await socketFactory.on(
        'recording:stopped',
        ({ data }) => {
          //  Filtre
          if (String(data.roomId) !== roomId) return;
          if (cancelled) return; // ✅ guard


          dispatch(setRecorderStatus('idle'));

          dispatch(setRecorderData({
            recorderName: null,
            isOwner: false,
            startedAt: null,
          }));

       
        }
      );

      const l3 = await socketFactory.on(
        'recording:sync',
        ({ data }) => {
          // ✅ Filtre
          if (String(data.roomId) !== roomId) return;
          if (cancelled) return; // ✅ guard


          const owner = data.userId === localAvatar?.id;
          dispatch(setRecorderData({
            recorderName: data.userName,
            isOwner: owner,
            startedAt: data.startedAt,
          }));
          dispatch(setRecorderStatus('recording'));
        }
      );

      //  Si le cleanup a déjà tourné avant la fin du register, on off immédiatement
      if (cancelledRef.current) {
        [l1, l2, l3].forEach(l => socketFactory.off(l));
        return;
      }

      listenersRef.current = [l1, l2, l3];
    };

    register();

    return () => {

      cancelledRef.current = true;

      socketFactory.socket?.emit('room:leave', { roomId });

      listenersRef.current.forEach(
        l => socketFactory.off(l)
      );

      listenersRef.current = [];

      // ✅ RESET REDUX
      dispatch(
        setRecorderStatus('idle')
      );

      dispatch(
        setRecorderData({
          recorderName: null,
          isOwner: false,
          startedAt: null,
        })
      );
    };

  }, [currentRoom?.room_id, localAvatar?.id, dispatch]);

  /* ──────────────────────────────────────────────
     ÉMISSIONS
  ────────────────────────────────────────────── */

  const notifyRecordingStarted = () => {
    if (!socketFactory.socket) return;

    socketFactory.socket.emit('recording:start', {
      roomId: String(currentRoom.room_id),
      userId: localAvatar.id,
      userName: localAvatar?.firstName || 'Utilisateur',
    });
  };

  const notifyRecordingStopped = () => {
    if (!socketFactory.socket) return;

    socketFactory.socket.emit('recording:stop', {
      roomId: String(currentRoom.room_id),
    });
  };

  return {
    notifyRecordingStarted,
    notifyRecordingStopped,
  };
}